import { useEffect, useState } from "react";
import { Search, Tag, ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useApi, useLoad } from "@/lib/api";
import { gbp } from "@/lib/format";
import { fmtDay, rangeQuery } from "@/lib/dates";
import { PageHead, Empty } from "@/components/Bits";
import DateRange from "@/components/DateRange";

const Stat = ({ label, s, testid, tone = "" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 font-mono text-2xl font-semibold ${tone}`}>{gbp(s.unit)}</p>
    {s.date && <p className="mt-1 truncate text-xs text-slate-500">{fmtDay(s.date)} · {s.supplier}</p>}
  </div>
);

// Arrow vs the previous (older) purchase of the same product.
const withChange = (rows) => rows.map((r, i) => {
  const prev = rows.slice(i + 1).find((x) => x.product === r.product);
  return { ...r, change: prev ? r.unit - prev.unit : 0 };
});

const Change = ({ c }) => (c > 0 ? <ArrowUpRight className="inline h-4 w-4 text-rose-600" /> : c < 0 ? <ArrowDownRight className="inline h-4 w-4 text-emerald-600" /> : null);

export default function PricesPage() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [range, setRange] = useState({ key: "all", from: null, to: null });
  const [supplier, setSupplier] = useState("");
  const [sugg, setSugg] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const suppliers = useLoad("/suppliers");

  useEffect(() => {
    if (q.trim().length < 2 || q === term) { setSugg([]); return; }
    const t = setTimeout(() => api("get", `/prices/products?q=${encodeURIComponent(q)}`).then(setSugg).catch(() => setSugg([])), 250);
    return () => clearTimeout(t);
  }, [q, term, api]);

  useEffect(() => {
    if (term.trim().length < 2) { setRes(null); return; }
    setBusy(true); setErr("");
    const qs = [`q=${encodeURIComponent(term)}`, rangeQuery(range), supplier && `supplier=${encodeURIComponent(supplier)}`].filter(Boolean).join("&");
    api("get", `/prices?${qs}`).then(setRes).catch((e) => setErr(e.message)).finally(() => setBusy(false));
  }, [term, range, supplier, api]);

  const go = (v) => { setQ(v); setTerm(v); setSugg([]); };
  const rows = res ? withChange(res.rows) : [];
  const st = res?.stats;

  return (
    <div>
      <PageHead title="Prices" sub="Search any item to see what you paid, when, and from which wholesaler. Prices are per unit, before VAT unless shown." />
      <form onSubmit={(e) => { e.preventDefault(); go(q); }} className="relative mb-3 rise">
        <Search className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. paracetamol 500" className="h-11 bg-white pl-10 text-base" data-testid="price-search-input" />
        {sugg.length > 0 && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="price-suggestions">
            {sugg.map((s, i) => (
              <button key={s} type="button" onClick={() => go(s)} className="block w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50" data-testid={`price-suggestion-${i}`}>{s}</button>
            ))}
          </div>
        )}
      </form>
      <div className="mb-6 grid gap-2 sm:grid-cols-[minmax(0,1fr)_14rem]">
        <DateRange value={range} onChange={setRange} testid="price-range" />
        <select value={supplier} onChange={(e) => setSupplier(e.target.value)} className="h-9 rounded-md border border-input bg-white px-3 text-sm" data-testid="price-supplier-filter">
          <option value="">All wholesalers</option>
          {(suppliers.data || []).map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
      </div>

      {!term && <Empty icon={Tag} title="Look up an item" text="Type part of a product name — every invoice line it appears on is listed, newest first." testid="prices-start" />}
      {busy && <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="prices-loading"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</p>}
      {err && <p className="text-sm text-rose-600" data-testid="prices-error">{err}</p>}
      {res && !busy && !rows.length && <Empty icon={Tag} title="Not found" text={`No invoice lines match "${term}" in this period.`} testid="prices-empty" />}

      {st && !busy && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4 rise" data-testid="price-stats">
            <Stat label="Last paid" s={st.latest} testid="price-stat-latest" />
            <Stat label="Lowest" s={st.lowest} testid="price-stat-lowest" tone="text-emerald-700" />
            <Stat label="Highest" s={st.highest} testid="price-stat-highest" tone="text-rose-600" />
            <Stat label={`Average (${st.qty} bought)`} s={{ unit: st.average }} testid="price-stat-average" />
          </div>
          {st.products.length > 1 && <p className="mb-3 text-xs text-slate-500" data-testid="price-products-note">Matches {st.products.length} products: {st.products.join(" · ")}</p>}
          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
            <table className="w-full text-sm" data-testid="price-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-2.5">Date</th><th>Product</th><th>Wholesaler</th><th>Invoice</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="px-4 text-right">Incl VAT</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`price-row-${i}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDay(r.date)}</td><td>{r.product}</td><td>{r.supplier}</td>
                    <td className="font-mono text-xs">{r.ref}</td><td className="text-right">{r.qty}</td>
                    <td className="text-right font-mono font-semibold"><Change c={r.change} /> {gbp(r.unit)}</td>
                    <td className="px-4 text-right font-mono text-slate-500">{gbp(r.unit_gross)} <span className="text-[10px]">({r.vat}%)</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2 md:hidden" data-testid="price-cards">
            {rows.map((r, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`price-card-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 text-sm font-semibold">{r.product}</p>
                  <p className="shrink-0 font-mono text-base font-semibold"><Change c={r.change} /> {gbp(r.unit)}</p>
                </div>
                <p className="mt-1 text-xs text-slate-500">{fmtDay(r.date)} · {r.supplier} · {r.ref} · qty {r.qty} · {gbp(r.unit_gross)} incl {r.vat}% VAT</p>
              </div>
            ))}
          </div>
          {res.truncated && <p className="mt-3 text-xs text-slate-500">Showing the latest 500 lines — narrow the dates to see older ones.</p>}
        </>
      )}
    </div>
  );
}
